import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import CleanLayout from '../../../components/common/layout/CleanLayout';
import Message from '../../../components/common/ui/Message';
import { IForgotPasswordFormValues } from '../../../components/login/ForgotPasswordForm';
import RegisterForm, {
  IRegisterFormProps,
  IRegisterFormValues,
} from '../../../components/login/RegisterForm';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PageJSONLDSchema } from '../../../models/app.models';
import {
  IBasePage,
  ICulture,
  ICustomerReviewsMeta,
  IMetaComponent,
  IRegisterPage,
  ServiceType,
} from '../../../models/strapi.models';
import { fetchStrapi, postStrapi } from '../../../services/api.service';
import { logIn } from '../../../services/auth.service';
import { logError, logInfo } from '../../../services/log.service';
import { getStoredData, storeData } from '../../../services/storage.service';
import { buildApiKey, getCommonTranslations } from '../../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../../shared/useJSONLDSchema';
import {
  clearPostLoginRedirect,
  getPostLoginRedirect,
} from '../../../shared/usePostLoginRedirect';
import { getTranslation } from '../../../shared/useUtils';
import {
  IAppPage,
  IAppPageLocalization,
  setIsLoading,
} from '../../../store/slices/appSlice';
import { NextPageWithLayout } from '../../_app';

interface IRegisterPageProps extends IBasePage {
  meta: IMetaComponent;
  labels: {
    nameLabel: string;
    emailLabel: string;
    passwordLabel: string;
    confirmPasswordLabel: string;
    newsletterCheckboxLabel: string;
    termsCheckboxLabel: string;
    submitButton: string;
    logInTip: string;
    logInButton: string;
    formSubmitSuccessMessage: string;
    formSubmitErrorMessage: string;
    formValidation: {
      requiredField: string;
      emailFormatNotValid: string;
      passwordsMustMatch: string;
      termsAndConditionsMustBeAccepted: string;
    };
  };
}

interface IRegisterPageState {
  submitResultType: 'success' | 'error' | undefined;
}

const RegisterPage: NextPageWithLayout<IRegisterPageProps> = (
  props: IRegisterPageProps
) => {
  const initialPageState: IRegisterPageState = {
    submitResultType: undefined,
  };
  const [state, setState] = useState(initialPageState);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const appPages = useAppSelector((state) => state.app.pages);
  const appLanguages = useAppSelector((state) => state.app.languages);
  const appCurrencies = useAppSelector((state) => state.app.currencies);
  const currentOrderState = useAppSelector((state) => state.currentOrder);

  const getRoute = useCallback(
    (actualPath: string, language?: string, slug?: string) => {
      let localizedRoute = actualPath;

      if (slug) {
        actualPath = actualPath.replace('[slug]', slug);

        if (language !== 'en') {
          actualPath = `/${language}${actualPath}`;
        }
      }

      if (language && slug && actualPath.includes('/blog/')) {
        // Here, the slug is the blog post id
        const page = appPages?.find((x: IAppPage) => {
          return x.destination.includes(slug);
        });

        if (page) {
          // Get its alternative page (localization) based on language
          const altPage = page?.localizations?.find(
            (x: IAppPageLocalization) => x.locale === language
          );

          if (altPage) {
            // Look for the localized page
            const localizedPage = appPages?.find((x: IAppPage) => {
              return x.destination.includes(altPage?.id.toString());
            });

            if (localizedPage) {
              localizedRoute = localizedPage.source;
            }
          }
        }
      } else {
        const localizedPage = appPages?.find(
          (x: IAppPage) =>
            x &&
            x.destination === actualPath &&
            (language ? x.language === language : x.language === router.locale)
        );

        if (localizedPage) {
          localizedRoute = localizedPage.source;
        }
      }

      logInfo(
        [{ actualPath, language, slug, localizedRoute }],
        'Register → Localized route'
      );

      return localizedRoute;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appPages, router.locale]
  );

  const getRegistrationCurrency = useCallback(async () => {
    let defaultCurrencyIsoCode = 'USD';
    const storedCurrencyIsoCode = getStoredData<{ code: string }>(
      'user-currency'
    )?.code;

    if (storedCurrencyIsoCode) {
      defaultCurrencyIsoCode = storedCurrencyIsoCode;
    } else {
      const culture = await fetchStrapi<ICulture>(`/users-info/culture`);
      const cultureCurrency = appCurrencies?.find(
        (c) => c.isoCode === culture?.currencyCode
      );

      if (!!cultureCurrency && !!cultureCurrency.isoCode) {
        defaultCurrencyIsoCode = cultureCurrency.isoCode;
      }
    }

    return appCurrencies?.find((c) => c.isoCode === defaultCurrencyIsoCode);
  }, [appCurrencies]);

  const registerUser = useCallback(
    async (data: IRegisterFormValues) => {
      dispatch(setIsLoading(true));

      const userRegistrationPayload = {
        email: data.email,
        username: data.email,
        password: data.password,
      };

      try {
        const userRegistrationResponse = (await postStrapi(
          '/auth/local/register',
          userRegistrationPayload
        )) as any;
        logInfo(
          ['Response', userRegistrationResponse],
          'RegisterPage → registerUser'
        );

        if (userRegistrationResponse.user) {
          const login = await logIn(
            data.email as string,
            data.password as string
          );
          const userLanguage = appLanguages.find(
            (x) => x.twoLetterIso === router.locale
          );
          const fallbackUserLanguage = appLanguages.find(
            (x) => x.twoLetterIso === 'en'
          );
          const userCurrency = await getRegistrationCurrency();
          const userInfoPayload = {
            data: {
              newsletter: data.newsletter,
              name: data.name,
              language: userLanguage
                ? userLanguage.id
                : fallbackUserLanguage!.id,
              currency: userCurrency!.id,
              user: userRegistrationResponse.user.id,
            },
          };
          const userInfoResponse = await postStrapi(
            '/users-info',
            userInfoPayload
          );
          logInfo(
            ['Response', userInfoResponse],
            'RegisterPage → registerUser'
          );
          setState((state) => ({ ...state, submitResultType: 'success' }));

          setTimeout(() => {
            let redirectUrl;

            if (currentOrderState.type === ServiceType.APPLICATION) {
              // Redirect if there is an ongoing order.
              redirectUrl = `${getRoute(
                '/trademark-registration',
                userLanguage?.twoLetterIso || 'en'
              )}`;
              storeData(2, 'checkout-step');
            } else if (currentOrderState.type === ServiceType.SEARCH) {
              // Redirect if there is an ongoing order.
              redirectUrl = `${getRoute(
                '/trademark-search',
                userLanguage?.twoLetterIso || 'en'
              )}`;
              storeData(2, 'checkout-step');
            } else {
              // Check sessionStorage or document.referrer for post-login redirect
              const savedRedirect = getPostLoginRedirect();
              if (savedRedirect) {
                redirectUrl = savedRedirect;
                clearPostLoginRedirect();
              } else {
                // Fallback → Navigate to Account → Profile
                redirectUrl = getRoute(
                  '/account/profile',
                  userLanguage?.twoLetterIso || 'en'
                );
              }
            }

            dispatch(setIsLoading(false));

            if (redirectUrl) {
              logInfo(
                ['Redirect to URL after register', redirectUrl],
                'Register page → registerUser'
              );
              window.location.href = `${window.location.origin}${redirectUrl}`;
            }
          }, 1000);
        }
      } catch (error) {
        dispatch(setIsLoading(false));
        setState((state) => ({ ...state, submitResultType: 'error' }));
        logError(['Error', error], 'RegisterPage → registerUser');
      }
    },
    [
      appLanguages,
      currentOrderState.type,
      dispatch,
      getRegistrationCurrency,
      getRoute,
      router,
    ]
  );

  const registerFormProps: IRegisterFormProps = useMemo<IRegisterFormProps>(
    () => ({
      labels: {
        nameLabel: props.labels?.nameLabel,
        emailLabel: props.labels?.emailLabel,
        passwordLabel: props.labels?.passwordLabel,
        confirmPasswordLabel: props.labels?.confirmPasswordLabel,
        newsletterCheckboxLabel: props.labels?.newsletterCheckboxLabel,
        termsCheckboxLabel: props.labels?.termsCheckboxLabel,
        submitButton: props.labels?.submitButton,
        logInTip: props.labels?.logInTip,
        logInButton: props.labels?.logInButton,
        formSubmitSuccessMessage: props.labels?.formSubmitSuccessMessage,
        formSubmitErrorMessage: props.labels?.formSubmitErrorMessage,
      },
      validation: {
        nameRequiredMessage: props.labels?.formValidation.requiredField,
        emailNotValidMessage: props.labels?.formValidation.emailFormatNotValid,
        emailRequiredMessage: props.labels?.formValidation.requiredField,
        passwordRequiredMessage: props.labels?.formValidation.requiredField,
        passwordConfirmationRequiredMessage:
          props.labels?.formValidation.requiredField,
        passwordNotMatchMessage:
          props.labels?.formValidation.passwordsMustMatch,
        termsRequiredMessage:
          props.labels?.formValidation.termsAndConditionsMustBeAccepted,
      },
      termsLink: getRoute('/terms', router.locale || 'en'),
      disabled: state.submitResultType === 'success',
      onFormSubmit: async (formData: IForgotPasswordFormValues) => {
        await registerUser(formData);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerUser, state.submitResultType]
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="robots" content="noindex, nofollow" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <RegisterForm {...registerFormProps} />
          <hr className="separator" />

          {(() => {
            switch (state.submitResultType) {
              case 'success':
                return (
                  <Message
                    type="success"
                    iconPath={'/assets/images/icons/EmailIcon.svg'}>
                    <ReactMarkdown>
                      {props.labels.formSubmitSuccessMessage as string}
                    </ReactMarkdown>
                  </Message>
                );
              case 'error':
                return (
                  <Message
                    type="danger"
                    iconPath={'/assets/images/icons/AlertIcon.svg'}>
                    <ReactMarkdown>
                      {props.labels.formSubmitErrorMessage as string}
                    </ReactMarkdown>
                  </Message>
                );
              default:
                return null;
            }
          })()}
        </div>
      </div>
    </div>
  );
};

RegisterPage.getLayout = function getLayout(page: ReactElement) {
  return <CleanLayout title={page.props.meta.title}>{page}</CleanLayout>;
};

export async function getStaticProps(
  context: any
): Promise<{ props: IRegisterPageProps }> {
  const pageUrlParams = new URLSearchParams({
    populate: '*',
  });

  const page = (
    await fetchStrapi<any>(`/register-page`, pageUrlParams, buildApiKey)
  ).data.attributes as IRegisterPage;
  const commonTranslations = await getCommonTranslations();

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const props: IRegisterPageProps = {
    pageSchema,
    meta: getTranslation(page.meta, context.locale),
    labels: {
      nameLabel: getTranslation(page.nameLabel, context.locale).text as string,
      emailLabel: getTranslation(page.emailLabel, context.locale)
        .text as string,
      passwordLabel: getTranslation(page.passwordLabel, context.locale)
        .text as string,
      confirmPasswordLabel: getTranslation(
        page.confirmPasswordLabel,
        context.locale
      ).text as string,
      newsletterCheckboxLabel: getTranslation(
        page.newsletterCheckboxLabel,
        context.locale
      ).text as string,
      termsCheckboxLabel: getTranslation(
        page.termsCheckboxLabel,
        context.locale
      ).text as string,
      submitButton: getTranslation(page.submitButton, context.locale)
        .text as string,
      logInTip: getTranslation(page.logInTip, context.locale).text as string,
      logInButton: getTranslation(page.logInButton, context.locale)
        .text as string,
      formSubmitSuccessMessage: getTranslation(
        page.formSubmitSuccessMessage,
        context.locale
      ).text as string,
      formSubmitErrorMessage: getTranslation(
        page.formSubmitErrorMessage,
        context.locale
      ).text as string,
      formValidation: {
        requiredField: getTranslation(
          commonTranslations.requiredField,
          context.locale
        ).text as string,
        emailFormatNotValid: getTranslation(
          commonTranslations.emailFormatNotValid,
          context.locale
        ).text as string,
        passwordsMustMatch: getTranslation(
          commonTranslations.passwordsMustMatch,
          context.locale
        ).text as string,
        termsAndConditionsMustBeAccepted: getTranslation(
          commonTranslations.termsAndConditionsMustBeAccepted,
          context.locale
        ).text as string,
      },
    },
  };

  return {
    props,
  };
}

export default RegisterPage;
